/**
 * SignInGate — global "please sign in to continue" modal.
 *
 * v78.59 — Conversion lift (P2). Previously, every gated action button
 * (Save / Subscribe / Chat with seller / Show phone) did a hard
 * `navigate("/login?redirect=...")` — which yanks the user out of the
 * listing they were exploring, blanks the screen, and forces them to
 * sign in before seeing why they were asked in the first place. That
 * pattern is brutal for conversion on marketplaces.
 *
 * The new pattern:
 *   1. Mount <SignInGate/> ONCE near the App root.
 *   2. Any component that needs auth calls `await requireAuth(reason)`.
 *      - If the user is already logged in, the promise resolves true
 *        immediately and the calling code proceeds.
 *      - If not, an inline modal opens, explains WHY we need auth,
 *        offers Sign In / Create Account buttons (both go to /auth
 *        with `?next=` pre-set to the current path), plus a "Maybe
 *        later" dismissal. The promise resolves false on dismissal
 *        so the caller knows to abort silently.
 *
 * Implementation: shared module-level state via a tiny event emitter,
 * no context provider needed. This keeps the helper callable from
 * non-component code paths too (e.g. axios interceptors).
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { ShieldCheck, Lock, X } from "lucide-react";

// Module-level state — there's only ever one gate mounted globally.
let openFn = null;

/**
 * Promise-based auth gate. Use inside event handlers:
 *   const ok = await requireAuth("Sign in to save listings");
 *   if (!ok) return;
 *   // user is now logged in OR was already logged in — safe to act.
 *
 * When the modal opens with a fresh login, the resolve happens *after*
 * the redirect roundtrip completes (the user comes back to the same
 * path because we passed ?next=<current>). For the "Maybe later" path,
 * the promise resolves with false immediately.
 */
export async function requireAuth(reason = "Sign in to continue") {
    if (typeof window === "undefined") return false;
    if (!openFn) return false;
    return openFn(reason);
}

export default function SignInGate() {
    const { user } = useAuth();
    const location = useLocation();
    const [state, setState] = useState({ open: false, reason: "" });
    const [pendingResolve, setPendingResolve] = useState(null);

    const handle = useCallback(
        (reason) => {
            // Already authed — short-circuit, never open the modal.
            if (user?.id) return Promise.resolve(true);
            return new Promise((resolve) => {
                setPendingResolve(() => resolve);
                setState({ open: true, reason });
            });
        },
        [user]
    );

    useEffect(() => {
        openFn = handle;
        return () => { openFn = null; };
    }, [handle]);

    // If the user becomes authed while the modal is open (e.g. they
    // signed in in another tab), auto-resolve and close.
    useEffect(() => {
        if (state.open && user?.id && pendingResolve) {
            pendingResolve(true);
            setPendingResolve(null);
            setState({ open: false, reason: "" });
        }
    }, [user, state.open, pendingResolve]);

    if (!state.open) return null;

    const dismiss = () => {
        if (pendingResolve) pendingResolve(false);
        setPendingResolve(null);
        setState({ open: false, reason: "" });
    };

    const next = encodeURIComponent(location.pathname + location.search);

    return (
        <div
            data-testid="sign-in-gate"
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-aduku-navy/70 backdrop-blur-sm"
            onClick={dismiss}
            role="dialog"
            aria-modal="true"
            aria-label="Sign in to continue"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-aduku-border overflow-hidden"
            >
                {/* Yellow accent ribbon */}
                <div className="bg-gradient-to-r from-aduku-yellow to-amber-300 px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-aduku-navy">
                        <ShieldCheck className="w-5 h-5" />
                        <span className="font-display font-black text-[15px] tracking-tight">Verified marketplace</span>
                    </div>
                    <button
                        onClick={dismiss}
                        data-testid="sign-in-gate-dismiss"
                        aria-label="Close"
                        className="w-7 h-7 rounded-full bg-aduku-navy/10 hover:bg-aduku-navy/20 text-aduku-navy flex items-center justify-center"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-5 sm:p-6">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-aduku-yellow/15 flex items-center justify-center shrink-0">
                            <Lock className="w-5 h-5 text-aduku-navy" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-display font-black text-aduku-navy text-[18px] leading-tight">
                                {state.reason || "Sign in to continue"}
                            </h3>
                            <p className="text-aduku-navy/65 text-[13px] mt-1 leading-relaxed">
                                Adukoo only allows verified humans to message, save, subscribe and reveal seller contact info — so the marketplace stays clean and spam-free.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Link
                            to={`/auth?mode=signin&next=${next}`}
                            data-testid="sign-in-gate-signin"
                            onClick={dismiss}
                            className="w-full h-11 rounded-xl bg-aduku-navy text-white font-bold inline-flex items-center justify-center gap-2 hover:brightness-110 transition"
                        >
                            Sign in
                        </Link>
                        <Link
                            to={`/auth?mode=signup&next=${next}`}
                            data-testid="sign-in-gate-signup"
                            onClick={dismiss}
                            className="w-full h-11 rounded-xl bg-aduku-yellow text-aduku-navy font-extrabold inline-flex items-center justify-center gap-2 hover:brightness-105 transition"
                            style={{ boxShadow: "0 0 30px rgba(249,192,21,0.35)" }}
                        >
                            Create account (free · 10 sec)
                        </Link>
                        <button
                            type="button"
                            onClick={dismiss}
                            data-testid="sign-in-gate-later"
                            className="w-full h-10 rounded-xl text-aduku-navy/60 hover:text-aduku-navy text-[13px] font-bold mt-1"
                        >
                            Maybe later
                        </button>
                    </div>

                    <div className="text-[11px] text-aduku-navy/50 mt-4 text-center">
                        We never sell your data · <Link to="/privacy" className="underline">Privacy</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
